/**
 * Profile Description Generator Utilities
 *
 * Generates dynamic profile descriptions by combining topStrength and secondStrength texts
 * from i18n translation files.
 */

import { StrengthProfile } from '../../../types/SkimmerDataTypes'
import { TFunction } from 'i18next'

const PROFILE_KEY_NORMALIZATIONS: Record<string, string> = {
  playerOverlapAirtimeTimesPreplaning: 'playerOverlapAirtimeTimesPrePlanning',
  playerOverlapAirtimeTimesPreplannin: 'playerOverlapAirtimeTimesPrePlanning',
  playerOverlapAirtimeTimesPreplanning: 'playerOverlapAirtimeTimesPrePlanning',
  playerTimesTalkedPreplanning: 'playerTimesTalkedPrePlanning',
  teamOverlapAirtimeTimesPreplaning: 'teamOverlapAirtimeTimesPrePlanning',
  teamOverlapAirtimeTimesPreplannin: 'teamOverlapAirtimeTimesPrePlanning',
  teamOverlapAirtimeTimesPreplanning: 'teamOverlapAirtimeTimesPrePlanning',
  teamTimesTalkedPreplanning: 'teamTimesTalkedPrePlanning'
}

export const normalizeProfileVariable = (variable: string): string => PROFILE_KEY_NORMALIZATIONS[variable] ?? variable

/**
 * Generates team profile description by combining topStrength + secondStrength texts
 *
 * @param topStrength - Top strength profile with variable name and normalized score
 * @param secondStrength - Second strength profile with variable name and normalized score
 * @param t - i18n translation function
 * @returns Combined description string
 *
 * @example
 * // If topStrength = playerSharedResource, secondStrength = playerAirtimeTotal
 * // Returns: "Your team shared the right resources at the right time, leaving room for every voice."
 */
export function generateTeamProfileDescription(
  topStrength: StrengthProfile,
  secondStrength: StrengthProfile,
  t: TFunction
): string {
  const normalizedTopKey = normalizeProfileVariable(topStrength.variable)
  const normalizedSecondKey = normalizeProfileVariable(secondStrength.variable)

  const topText = t(`team.profile.${normalizedTopKey}.topStrengthText`, {
    ns: 'feedbackTexts',
    defaultValue: ''
  })

  const secondText = t(`team.profile.${normalizedSecondKey}.secondStrengthText`, {
    ns: 'feedbackTexts',
    defaultValue: ''
  })

  // Combine: topStrength text + space + secondStrength text
  // Both texts already have proper punctuation from the JSON
  return `${topText} ${secondText}`.trim()
}

/**
 * Generates individual profile description by combining topStrength + secondStrength texts
 *
 * @param topStrength - Top strength profile with variable name and normalized score
 * @param secondStrength - Second strength profile with variable name and normalized score
 * @param t - i18n translation function
 * @returns Combined description string
 *
 * @example
 * // Similar to team profile but uses individual.profile path
 */
export function generateIndividualProfileDescription(
  topStrength: StrengthProfile,
  secondStrength: StrengthProfile,
  t: TFunction
): string {
  const normalizedTopKey = normalizeProfileVariable(topStrength.variable)
  const normalizedSecondKey = normalizeProfileVariable(secondStrength.variable)

  const topText = t(`individual.profile.${normalizedTopKey}.topStrengthText`, {
    ns: 'feedbackTexts',
    defaultValue: ''
  })

  const secondText = t(`individual.profile.${normalizedSecondKey}.secondStrengthText`, {
    ns: 'feedbackTexts',
    defaultValue: ''
  })

  return `${topText} ${secondText}`.trim()
}
